import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { subscriptionsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, XCircle, CheckCircle, Loader2, Copy, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';

const CancelWorkflow = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [reason, setReason] = useState('');
  const [instructions, setInstructions] = useState(null);
  const [potentialSavings, setPotentialSavings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [id]);

  const loadSubscription = async () => {
    try {
      const response = await subscriptionsApi.getOne(id);
      setSubscription(response.data);
    } catch (error) {
      toast.error('Failed to load subscription');
      navigate('/subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateInstructions = async () => {
    setIsGenerating(true);
    try {
      const response = await subscriptionsApi.startCancelWorkflow(id, reason);
      setInstructions(response.data.instructions);
      setPotentialSavings(response.data.potential_savings);
      toast.success('Cancellation instructions generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate instructions');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleMarkCancelled = async () => {
    setIsCompleting(true);
    try {
      const response = await subscriptionsApi.markCancelled(id);
      toast.success(`Subscription cancelled! You'll save $${response.data.savings}/year`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to mark subscription as cancelled');
    } finally {
      setIsCompleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="cancel-workflow-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              Cancel Subscription
            </h1>
            <p className="text-gray-500">Get AI-powered cancellation instructions</p>
          </div>
        </div>

        {/* Subscription Info */}
        <Card className="border border-gray-100 shadow-soft">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#D1FAE5] rounded-2xl flex items-center justify-center text-[#064E3B] text-xl font-bold">
                  {subscription?.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{subscription?.name}</h2>
                  <p className="text-gray-500">
                    ${subscription?.amount}/{subscription?.billing_cycle}
                  </p>
                </div>
              </div>
              <Badge className="bg-[#D1FAE5] text-[#064E3B] border-0 px-4 py-1">
                Active
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Potential Savings Banner */}
        {potentialSavings > 0 && (
          <Card className="border-0 bg-gradient-to-r from-[#FEF3C7] to-[#FDE68A]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/50 rounded-xl flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-[#D97706]" />
                </div>
                <div>
                  <p className="font-medium text-[#92400E]">Potential Annual Savings</p>
                  <p className="text-2xl font-bold text-[#78350F]">${potentialSavings}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Reason */}
        {!instructions && (
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
                Step 1: Why do you want to cancel?
              </CardTitle>
              <CardDescription>
                This helps us generate better cancellation instructions (optional)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for cancellation</Label>
                <Textarea
                  id="reason"
                  placeholder="e.g., I don't use it anymore, too expensive, found a better alternative..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="bg-gray-50 border-gray-200 focus:bg-white min-h-24"
                  data-testid="reason-input"
                />
              </div>
              <Button
                onClick={handleGenerateInstructions}
                disabled={isGenerating}
                className="w-full bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Instructions...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Generate Cancellation Instructions
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Instructions */}
        {instructions && (
          <Card className="border border-gray-100 shadow-soft">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>
                    Step 2: Follow these instructions
                  </CardTitle>
                  <CardDescription>
                    AI-generated cancellation guide for {subscription?.name}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(instructions)}
                  className="rounded-lg"
                  data-testid="copy-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="prose prose-sm max-w-none bg-gray-50 rounded-xl p-6">
                <div className="whitespace-pre-wrap text-gray-700" data-testid="instructions-content">
                  {instructions}
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                  Step 3: Confirm cancellation
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  Once you've successfully cancelled, click below to update your records and track your savings.
                </p>
                <Button
                  onClick={handleMarkCancelled}
                  disabled={isCompleting}
                  className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl btn-hover"
                  data-testid="complete-btn"
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      I've Cancelled - Track My Savings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CancelWorkflow;
